/* See UserWizardProfileStep: labels are associated explicitly via htmlFor + the
   control's DOM id, so the description text below a dropdown stays outside the
   <label> instead of acting as a second click target for it. */
/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Select from 'src/components/ui/Select/Select';
import { UserData } from '../../services/user';
import { mapRolesToPermissions } from '../../utils/general';
import {
  PERMISSION_AREA_KEYS,
  PERMISSION_AREA_LABEL_KEYS,
  PERMISSION_LEVEL_LABEL_KEYS,
  PermissionAreaKey,
  PermissionLevel,
  getSelectableLevels,
} from './userWizardLogic';

/**
 * Permissions of an existing user (edit/view). Creating a user runs through
 * UserCreateWizard instead, which is why there is no password field here any more.
 *
 * The form keeps storing each right as a `(view, admin)` boolean pair so the
 * shared transforms — mapRolesToPermissions / transformPermissionsToRoles — and
 * their tests stay untouched; the dropdown derives a single none/read/read_write
 * level from that pair and writes it back.
 */
const generalFormSchema = z.object({
  permissions: z.object({
    outbound_view: z.boolean(),
    outbound_admin: z.boolean(),
    products_view: z.boolean(),
    products_admin: z.boolean(),
    returns_view: z.boolean(),
    returns_admin: z.boolean(),
    carrier_view: z.boolean(),
    carrier_admin: z.boolean(),
    integrations_view: z.boolean(),
    integrations_admin: z.boolean(),
    cases_view: z.boolean(),
    cases_admin: z.boolean(),
  }),
});

type GeneralFormData = z.infer<typeof generalFormSchema>;

export interface UserGeneralFormRef {
  submit: () => Promise<GeneralFormData | null>;
  getCurrentValues: () => GeneralFormData;
  isValid: () => boolean;
}

type Props = {
  readOnly?: boolean;
  onSubmit?: (data: GeneralFormData) => void;
  initialData?: UserData | null | undefined;
};

const PERMISSION_DESCRIPTION_KEYS: Record<PermissionAreaKey, { read: string; read_write: string }> = {
  outbound: { read: 'general.outbound_view_description', read_write: 'general.outbound_admin_description' },
  products: { read: 'general.products_view_description', read_write: 'general.products_admin_description' },
  returns: { read: 'general.returns_view_description', read_write: 'general.returns_admin_description' },
  carrier: { read: 'general.carrier_view_description', read_write: 'general.carrier_admin_description' },
  integrations: {
    read: 'general.integrations_view_description',
    read_write: 'general.integrations_admin_description',
  },
  cases: { read: 'general.cases_view_description', read_write: 'general.cases_admin_description' },
};

const permissionSelectId = (area: PermissionAreaKey) => `user-permission-${area}`;

export const UserGeneralForm = forwardRef<UserGeneralFormRef, Props>(({ readOnly, onSubmit, initialData }, ref) => {
  const { t } = useTranslation();

  const { handleSubmit, getValues, formState, setValue, watch, reset } = useForm<GeneralFormData>({
    resolver: zodResolver(generalFormSchema),
    defaultValues: {
      permissions: {
        outbound_view: false,
        outbound_admin: false,
        products_view: false,
        products_admin: false,
        returns_view: false,
        returns_admin: false,
        carrier_view: false,
        carrier_admin: false,
        integrations_view: false,
        integrations_admin: false,
        cases_view: true,
        cases_admin: false,
      },
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({ permissions: mapRolesToPermissions(initialData.roles) });
    }
  }, [initialData]);

  const watchedPermissions = watch('permissions');

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise<GeneralFormData | null>(resolve => {
        handleSubmit(
          data => resolve(data),
          errors => {
            console.error('General form validation errors:', errors);
            resolve(null);
          },
        )();
      }),
    getCurrentValues: () => getValues(),
    isValid: () => formState.isValid,
  }));

  const getPermissionLevel = (area: PermissionAreaKey): PermissionLevel => {
    if (watchedPermissions[`${area}_admin` as keyof typeof watchedPermissions]) return 'read_write';
    if (watchedPermissions[`${area}_view` as keyof typeof watchedPermissions]) return 'read';
    return 'none';
  };

  const handlePermissionLevelChange = (area: PermissionAreaKey, level: PermissionLevel) => {
    setValue(`permissions.${area}_view` as any, level === 'read' || level === 'read_write');
    setValue(`permissions.${area}_admin` as any, level === 'read_write');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit as any)} className="flex w-full flex-col gap-16">
      <div className="flex w-full flex-col gap-24">
        <h3 className="typo-text-16--bold text-full">{t('general.permissions')}</h3>
        <div className="flex w-full flex-col gap-16">
          {PERMISSION_AREA_KEYS.map(area => {
            const level = getPermissionLevel(area);
            const selectableLevels = getSelectableLevels(area);
            const fallbackLevel = selectableLevels[0];
            const options = selectableLevels.map(selectable => ({
              value: selectable,
              label: t(PERMISSION_LEVEL_LABEL_KEYS[selectable]),
            }));
            const description = level === 'none' ? '' : t(PERMISSION_DESCRIPTION_KEYS[area][level]);

            return (
              <div key={area} className="grid w-full grid-cols-3 items-start gap-12">
                <label htmlFor={permissionSelectId(area)} className="typo-text-14--bold text-faded col-span-1">
                  {t(PERMISSION_AREA_LABEL_KEYS[area])}
                </label>
                <span className="col-span-2 flex flex-col gap-4">
                  <Select
                    id={permissionSelectId(area)}
                    options={options}
                    defaultValue={options.find(option => option.value === level)}
                    onChange={option =>
                      handlePermissionLevelChange(area, (option?.value as PermissionLevel) ?? fallbackLevel)
                    }
                    disabled={readOnly}
                    fullWidth
                  />
                  {description && <span className="typo-text-12 text-faded">{description}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </form>
  );
});

export default UserGeneralForm;
